import React, { useState, useMemo, useRef } from 'react';
import axios from 'axios';
import { Send, Loader2, Mail, MessageCircle, PhoneCall, CheckCircle2, X, Copy, RotateCw } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';

/**
 * ContactLGRASection — public "Contact LGRA" form rendered just before
 * the site-wide `PublicFooter` on the homepage. Anchored at `#contact`
 * so the "Contact LGRA" link in the Explore LGRA dropdown scrolls here.
 *
 * Data flow (Msg 216 · Feb 2026):
 *  1. Client-side validation + honeypot; hard-lock button via ref during
 *     the POST so a rapid double-click cannot fire two DB inserts.
 *  2. POST /api/enquiries → 200 { reference, message, preferred_contact,
 *     whatsapp:{ url, text } }. The enquiry is saved server-side FIRST
 *     and the admin + customer confirmation emails run in the backend's
 *     background — the browser never opens the customer's email app.
 *  3. Success flow branches on `preferred_contact`:
 *      • WhatsApp — open the prefilled WhatsApp URL in a new tab AFTER
 *        the save succeeded. If the popup is blocked the modal still
 *        surfaces a big "Open WhatsApp with your message" fallback CTA
 *        (a direct user gesture, so the second attempt goes through).
 *      • Email / Phone — no WhatsApp open. Modal shows the correct
 *        expectation copy ("reply within 24 hours" / "call within 24 hours").
 *  4. On save failure the button re-enables and a Retry button appears
 *     next to the error banner so the customer can submit again without
 *     re-typing anything.
 */

const API = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_CATEGORIES = [
  'General Enquiry',
  'Class Booking',
  'Private Coaching',
  'Membership / Family Plans',
  'Media / Partnership',
  'Complaint / Feedback',
  'Other',
];

const PREFERRED_CONTACT = [
  { key: 'WhatsApp', label: 'WhatsApp', short: 'WA', Icon: MessageCircle },
  { key: 'Email', label: 'Email', short: 'Email', Icon: Mail },
  { key: 'Phone Call', label: 'Phone', short: 'Call', Icon: PhoneCall },
];

const initialForm = {
  name: '',
  email: '',
  phone: '',
  category: 'General Enquiry',
  message: '',
  preferred_contact: 'WhatsApp',
  honeypot_website: '',
};

const ContactLGRASection = () => {
  const [form, setForm] = useState(initialForm);
  const [busy, setBusy] = useState(false);
  const [successRef, setSuccessRef] = useState(null);
  const [fieldErr, setFieldErr] = useState('');
  const [submitFailed, setSubmitFailed] = useState(false);
  const submittingRef = useRef(false);  // hard lock across rapid double-clicks
  const formTopRef = useRef(null);

  const disabled = useMemo(
    () => busy || !form.name.trim() || !form.email.trim()
             || !form.phone.trim() || form.message.trim().length < 5,
    [busy, form],
  );

  const update = (key) => (e) => {
    const v = e && e.target ? e.target.value : e;
    setForm((f) => ({ ...f, [key]: v }));
    if (fieldErr) setFieldErr('');
    if (submitFailed) setSubmitFailed(false);
  };

  const submitEnquiry = async () => {
    // Duplicate-submit guard — busy/disabled updates React state async
    // so a fast double-click can slip through before setBusy(true) commits.
    if (submittingRef.current) return;
    submittingRef.current = true;
    setBusy(true);
    setFieldErr('');
    setSubmitFailed(false);
    try {
      const source_page = (typeof window !== 'undefined'
        ? window.location.pathname + window.location.hash
        : '/');
      const { data } = await axios.post(`${API}/api/enquiries`, {
        ...form,
        source_page,
      });
      const preferred = data.preferred_contact || form.preferred_contact;
      setSuccessRef({
        reference: data.reference,
        preferred_contact: preferred,
        whatsapp: data.whatsapp || null,
        name: form.name,
      });
      // ONLY open WhatsApp after the save has returned 200. If the
      // popup is blocked the modal still exposes the fallback CTA.
      if (preferred === 'WhatsApp' && data?.whatsapp?.url) {
        try { window.open(data.whatsapp.url, '_blank', 'noopener'); }
        catch { /* popup blocked — modal fallback handles it */ }
      }
      setForm(initialForm);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail
                   || err?.response?.data?.message
                   || 'We could not send your enquiry. Please try again or WhatsApp us.';
      if (status === 429) {
        toast.error(detail);
      } else if (status === 400) {
        setFieldErr(detail);
        toast.error(detail);
      } else {
        setSubmitFailed(true);
        setFieldErr(detail);
      }
    } finally {
      submittingRef.current = false;
      setBusy(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (disabled) return;
    await submitEnquiry();
  };

  const handleRetry = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (busy) return;
    await submitEnquiry();
  };

  const copyRef = async () => {
    if (!successRef?.reference) return;
    try {
      await navigator.clipboard.writeText(successRef.reference);
      toast.success('Reference copied.');
    } catch {
      toast.error('Copy failed — long-press to select.');
    }
  };

  const reopenWhatsApp = () => {
    if (!successRef?.whatsapp?.url) return;
    // Direct user gesture — will not be blocked even if the auto-open was.
    window.open(successRef.whatsapp.url, '_blank', 'noopener');
  };

  // Copy strings that vary by preferred_contact — mirrors the backend
  // confirmation email so the customer's website and inbox agree.
  const preferredCopy = (() => {
    const p = successRef?.preferred_contact || 'WhatsApp';
    if (p === 'WhatsApp') {
      return {
        headline: 'Enquiry received!',
        subtitle: "We've opened WhatsApp with your message. If it didn't open, tap the button below.",
        primaryCta: 'Open WhatsApp with your message',
        showWhatsAppCta: true,
      };
    }
    if (p === 'Phone Call') {
      return {
        headline: 'Enquiry received!',
        subtitle: 'Your enquiry has been submitted. The LGRA team will contact you by phone within 24 hours.',
        primaryCta: null,
        showWhatsAppCta: false,
      };
    }
    // Email
    return {
      headline: 'Enquiry received!',
      subtitle: 'Your enquiry has been submitted. The LGRA team will reply to your email within 24 hours.',
      primaryCta: null,
      showWhatsAppCta: false,
    };
  })();

  return (
    <section
      id="contact"
      ref={formTopRef}
      className="relative bg-gradient-to-b from-white via-[#FAF5FF] to-white py-16 sm:py-20"
      data-testid="contact-lgra-section"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 lg:gap-14 items-start">
          {/* Left — Copy + direct contact CTAs */}
          <div>
            <div className="text-[11px] font-black uppercase tracking-widest text-[#7C3AED] mb-2">
              Contact LGRA
            </div>
            <h2 className="text-4xl sm:text-5xl font-black tracking-tight text-[#0F172A] leading-[1.05]">
              Say hi to the LGRA team.
            </h2>
            <p className="mt-4 text-base text-slate-600 leading-relaxed max-w-md">
              Class questions, coaching plans, media, or feedback — one form,
              one team. We usually reply within 24 hours (often much sooner
              on WhatsApp).
            </p>
            <div className="mt-8 flex flex-col gap-3" data-testid="contact-direct-ctas">
              <a
                href="https://wa.link/qtx1ba"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 px-5 h-14 rounded-2xl bg-[#22C55E] hover:bg-[#16A34A] text-white font-black tracking-tight transition-colors shadow-sm shadow-emerald-500/20"
                data-testid="contact-whatsapp-jason"
              >
                <MessageCircle size={20} strokeWidth={2.4} />
                <span className="flex-1 text-left leading-tight">
                  <span className="block text-xs uppercase tracking-widest opacity-90">WhatsApp Jason</span>
                  <span className="block text-base">+60 16-255 4068</span>
                </span>
              </a>
              <a
                href="https://wa.link/o8g0zf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 px-5 h-14 rounded-2xl bg-white border-2 border-emerald-500 text-[#065F46] hover:bg-emerald-50 font-black tracking-tight transition-colors"
                data-testid="contact-whatsapp-kelly"
              >
                <MessageCircle size={20} strokeWidth={2.4} className="text-emerald-600" />
                <span className="flex-1 text-left leading-tight">
                  <span className="block text-xs uppercase tracking-widest text-emerald-700/80">WhatsApp Kelly</span>
                  <span className="block text-base">+60 17-699 5214</span>
                </span>
              </a>
              <a
                href="mailto:info@lgra.com"
                className="inline-flex items-center gap-3 px-5 h-14 rounded-2xl bg-white border-2 border-slate-200 hover:border-[#7C3AED] hover:bg-[#F5F3FF] text-slate-800 font-black tracking-tight transition-colors"
                data-testid="contact-email"
              >
                <Mail size={20} strokeWidth={2.4} className="text-[#7C3AED]" />
                <span className="flex-1 text-left leading-tight">
                  <span className="block text-xs uppercase tracking-widest text-[#7C3AED]/80">Email</span>
                  <span className="block text-base">info@lgra.com</span>
                </span>
              </a>
            </div>
          </div>

          {/* Right — Contact form */}
          <form
            onSubmit={handleSubmit}
            noValidate
            className="bg-white rounded-3xl border border-slate-200 shadow-[0_10px_40px_-15px_rgba(124,58,237,0.25)] p-6 sm:p-8"
            data-testid="contact-form"
            aria-label="Contact LGRA form"
          >
            {/* Honeypot — hidden from screen readers + tab order + visually. */}
            <div
              aria-hidden="true"
              style={{ position: 'absolute', left: '-10000px', top: 'auto',
                       width: 1, height: 1, overflow: 'hidden' }}
            >
              <label htmlFor="contact-hp-website">Website</label>
              <input
                id="contact-hp-website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={form.honeypot_website}
                onChange={update('honeypot_website')}
                data-testid="contact-honeypot"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="contact-name" className="text-xs font-black uppercase tracking-widest text-slate-600">
                  Your name <span className="text-red-500">*</span>
                </label>
                <input
                  id="contact-name"
                  type="text"
                  required
                  maxLength={80}
                  value={form.name}
                  onChange={update('name')}
                  placeholder="Full name"
                  className="mt-1.5 w-full h-11 px-3 rounded-lg border border-slate-200 focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/20 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-colors"
                  data-testid="contact-input-name"
                />
              </div>
              <div>
                <label htmlFor="contact-phone" className="text-xs font-black uppercase tracking-widest text-slate-600">
                  Phone <span className="text-red-500">*</span>
                </label>
                <input
                  id="contact-phone"
                  type="tel"
                  required
                  maxLength={25}
                  value={form.phone}
                  onChange={update('phone')}
                  placeholder="+60 12 345 6789"
                  className="mt-1.5 w-full h-11 px-3 rounded-lg border border-slate-200 focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/20 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-colors"
                  data-testid="contact-input-phone"
                />
              </div>
            </div>

            <div className="mt-4">
              <label htmlFor="contact-email" className="text-xs font-black uppercase tracking-widest text-slate-600">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                id="contact-email"
                type="email"
                required
                maxLength={120}
                value={form.email}
                onChange={update('email')}
                placeholder="you@example.com"
                className="mt-1.5 w-full h-11 px-3 rounded-lg border border-slate-200 focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/20 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-colors"
                data-testid="contact-input-email"
              />
            </div>

            <div className="mt-4 grid sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="contact-category" className="text-xs font-black uppercase tracking-widest text-slate-600">
                  What's it about?
                </label>
                <select
                  id="contact-category"
                  value={form.category}
                  onChange={update('category')}
                  className="mt-1.5 w-full h-11 px-3 rounded-lg border border-slate-200 focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/20 text-sm text-slate-900 bg-white outline-none transition-colors"
                  data-testid="contact-select-category"
                >
                  {DEFAULT_CATEGORIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div>
                <div className="text-xs font-black uppercase tracking-widest text-slate-600">
                  Preferred reply
                </div>
                <div className="mt-1.5 grid grid-cols-3 gap-1.5" role="radiogroup" aria-label="Preferred contact method">
                  {PREFERRED_CONTACT.map(({ key, label, short, Icon }) => {
                    const active = form.preferred_contact === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        onClick={() => setForm((f) => ({ ...f, preferred_contact: key }))}
                        className={`h-11 rounded-lg border text-[11px] font-black tracking-tight inline-flex items-center justify-center gap-1 px-1 transition-colors overflow-hidden ${
                          active
                            ? 'bg-[#F5F3FF] border-[#7C3AED] text-[#5B21B6]'
                            : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                        }`}
                        data-testid={`contact-pref-${key.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <Icon size={13} className="shrink-0" />
                        <span className="min-[420px]:hidden truncate">{short}</span>
                        <span className="hidden min-[420px]:inline truncate">{label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="mt-4">
              <label htmlFor="contact-message" className="text-xs font-black uppercase tracking-widest text-slate-600">
                Message <span className="text-red-500">*</span>
              </label>
              <textarea
                id="contact-message"
                required
                minLength={5}
                maxLength={2000}
                rows={5}
                value={form.message}
                onChange={update('message')}
                placeholder="Tell us what you're looking for — class age group, preferred day, budget, or anything else."
                className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/20 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-colors resize-y"
                data-testid="contact-input-message"
              />
              <div className="mt-1 text-[11px] text-slate-400 text-right">
                {form.message.length} / 2000
              </div>
            </div>

            {fieldErr && (
              <div
                className="mt-3 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800 flex items-start justify-between gap-3"
                data-testid="contact-field-error"
              >
                <span className="flex-1">{fieldErr}</span>
                {submitFailed && (
                  <button
                    type="button"
                    onClick={handleRetry}
                    disabled={busy}
                    className="shrink-0 inline-flex items-center gap-1 text-xs font-black uppercase tracking-widest text-red-700 hover:text-red-900 disabled:opacity-60"
                    data-testid="contact-retry-btn"
                  >
                    <RotateCw size={12} /> Retry
                  </button>
                )}
              </div>
            )}

            <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="text-xs text-slate-500 leading-snug">
                We reply within 24 hours (Mon–Sat). Faster on WhatsApp.
              </div>
              <Button
                type="submit"
                disabled={disabled}
                className="h-12 px-6 rounded-xl bg-[#5530B5] hover:bg-[#3F2774] text-white font-black tracking-tight inline-flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="contact-submit-btn"
              >
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                {busy ? 'Sending…' : 'Send enquiry'}
              </Button>
            </div>
          </form>
        </div>
      </div>

      {/* Success modal — full-screen on mobile, dialog on desktop. */}
      <Dialog
        open={!!successRef}
        onOpenChange={(o) => { if (!o) setSuccessRef(null); }}
      >
        <DialogContent
          className="max-w-lg p-0 overflow-hidden"
          data-testid="contact-success-modal"
        >
          <div className="bg-gradient-to-br from-[#F5F3FF] via-white to-[#EDE9FE] p-6 sm:p-8">
            <DialogHeader>
              <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 border-2 border-emerald-300 flex items-center justify-center mb-3">
                <CheckCircle2 className="text-emerald-600" size={30} />
              </div>
              <DialogTitle
                className="text-center text-2xl sm:text-3xl font-black tracking-tight text-[#0F172A]"
                data-testid="contact-success-headline"
              >
                {preferredCopy.headline}
              </DialogTitle>
              <DialogDescription
                className="text-center text-slate-600 mt-1"
                data-testid="contact-success-subtitle"
              >
                {preferredCopy.subtitle}
              </DialogDescription>
            </DialogHeader>

            <div
              className="mt-5 rounded-xl border-2 border-dashed border-[#A855F7]/40 bg-white p-4 text-center"
              data-testid="contact-success-reference-block"
            >
              <div className="text-[10px] font-black uppercase tracking-widest text-[#7C3AED]">
                Reference
              </div>
              <div
                className="mt-1 font-mono text-xl sm:text-2xl font-black tracking-widest text-[#5B21B6]"
                data-testid="contact-success-reference"
              >
                {successRef?.reference}
              </div>
              <button
                type="button"
                onClick={copyRef}
                className="mt-3 inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-widest text-[#7C3AED] hover:text-[#5B21B6] transition-colors"
                data-testid="contact-copy-reference"
              >
                <Copy size={12} /> Copy reference
              </button>
            </div>

            <div className="mt-5 text-xs text-slate-500 leading-relaxed text-center">
              Quote this reference if you WhatsApp or email us later — it lets
              our team pull up your enquiry in one click. A copy has also been
              emailed to you.
            </div>

            <div className="mt-6 grid grid-cols-1 gap-2">
              {preferredCopy.showWhatsAppCta && (
                <button
                  type="button"
                  onClick={reopenWhatsApp}
                  className="inline-flex items-center justify-center gap-1.5 h-11 rounded-lg bg-[#22C55E] hover:bg-[#16A34A] text-white font-black tracking-tight text-sm transition-colors"
                  data-testid="contact-success-open-whatsapp"
                >
                  <MessageCircle size={14} /> {preferredCopy.primaryCta}
                </button>
              )}
              <button
                type="button"
                onClick={() => setSuccessRef(null)}
                className="inline-flex items-center justify-center gap-1.5 h-11 rounded-lg bg-white border-2 border-slate-200 hover:border-slate-300 text-slate-700 font-black tracking-tight text-sm transition-colors"
                data-testid="contact-success-close"
              >
                <X size={14} /> Close
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default ContactLGRASection;
